import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { ESSAdapter, TypeaheadSelection, TypeaheadToolbarVersion, UitkTypeahead } from 'uitk-react-typeahead';
import { useStores } from '../../utils/hooks';
import { getPageId } from '../../helpers/common-helper';
import { ActivityDestinationViewSearch, ActivityGroupViewType } from '../../common/__generated__/api';

const NOT_FOUND_INDEX = -1;

export interface ActivityTypeaheadSearchInputProps {
  isCarousel?: boolean;
}

export const ActivityTypeaheadSearchInput: React.FC<ActivityTypeaheadSearchInputProps> = observer(({ isCarousel }) => {
  const { analytics, activitySearchStore, routeStore, context } = useStores();
  const tileIndex = (activitySearchStore.activityGroups as ActivityDestinationViewSearch.ActivityGroups[]).findIndex(
    activityGroup => activityGroup.viewType === ActivityGroupViewType.KEYWORD_SEARCH
  );
  let keywordData = {
    analytics: {
      linkName: 'keyword-search-done',
      referrerId: `${getPageId(activitySearchStore)}.filterSort.SearchActivity`
    },
    placeholder: activitySearchStore.sortAndFilterObject.keywordPlaceholder,
    keyword: activitySearchStore.sortAndFilterObject.keywordValue
  };

  if (isCarousel && tileIndex > NOT_FOUND_INDEX) {
    keywordData = {
      analytics: activitySearchStore.activityGroups[tileIndex].activityTiles[0].analytics,
      placeholder: activitySearchStore.activityGroups[tileIndex].activityTiles[0].placeholder || '',
      keyword: activitySearchStore.activityGroups[tileIndex].activityTiles[0].searchTerm || ''
    };
  }

  const domain = activitySearchStore.essDomain;

  const updateTypeahead = (typeaheadResult: TypeaheadSelection): void | null => {
    const filterKeyWord = typeaheadResult.term;
    keywordData.keyword = filterKeyWord;
    activitySearchStore.sortAndFilterObject.keywordValue = filterKeyWord;
    const newQueryString = routeStore.mergeQueryString(routeStore.search, {
      slimSearchKeyword: filterKeyWord
    });
    if (typeaheadResult.data) {
      analytics.trackEvent(
        `SR.filterSort.SearchActivity.${filterKeyWord}`,
        'searchActivity-typeahead-search-done',
        analytics.omnitureData,
        null,
        true
      );
    }
    if (isCarousel) {
      routeStore.updateSearch(newQueryString, {}, true);
    }
  };

  const handleClear = (): void => {
    activitySearchStore.sortAndFilterObject.keywordValue = '';
  };

  const rid =
    routeStore && routeStore.queryParams && routeStore.queryParams.rid
      ? parseInt(routeStore.queryParams.rid, 10)
      : undefined;

  const activityTypeaheadAdapter = {
    adapter: new ESSAdapter({
      client: 'LXActivitiesSearchPage',
      dest: true,
      domain: domain,
      features: '',
      guid: context && context.deviceId,
      lob: 'ACTIVITIES',
      locale: context && context.locale,
      maxResults: 10,
      regionType: 2048,
      siteId: context && context.site.id,
      regionId: rid
    }),
    label: keywordData.placeholder,
    typeaheadPlaceholder: keywordData.placeholder
  };

  return (
    <UitkTypeahead
      adapter={activityTypeaheadAdapter.adapter}
      allowExactMatch={true}
      icon="search"
      label={activityTypeaheadAdapter.label}
      name="searchKeyWord"
      onUpdate={updateTypeahead}
      stid="typeahead-adaptive"
      data-testid="typeahead-adaptive"
      toolbarVersion={TypeaheadToolbarVersion.V2}
      handleClear={handleClear}
      hasClearButton={true}
      value={keywordData.keyword}
      typeaheadPlaceholder={activityTypeaheadAdapter.typeaheadPlaceholder}
    />
  );
});
